import { PreferenceComputed } from '@state/modules/preference'
import Constants from '@constants'
import { LicenseComputed } from '@state/modules/license'
export const taskPermissionsMixin = {
  computed: {
    ...PreferenceComputed,
    ...LicenseComputed,
  },
  methods: {
    /**
     * Gets the permission string based on module name and action
     */
    getTaskPermissionString(moduleName, action) {
      const moduleMap = {
        [Constants.REQUEST]: `request.task.${action}`,
        [Constants.PROBLEM]: `problem.task.${action}`,
        [Constants.CHANGE]: `change.task.${action}`,
        [Constants.RELEASE]: `release.task.${action}`,
        [Constants.PROJECT]: `project.task.${action}`,
        [Constants.TASK]: `task.${action}`,
      }
      return moduleMap[moduleName] || `task.${action}`
    },

    /**
     * Base method to check task permissions
     */
    hasTaskPermission(moduleName, action) {
      if (
        moduleName === Constants.PROJECT &&
        this.availableModulesInLicense.indexOf(Constants.PROJECT) >= 0 &&
        this.hasProjectRequesterLicense
      ) {
        return true
      }
      const permissionString = this.getTaskPermissionString(moduleName, action)
      return this.myAllowedModules.indexOf(permissionString) !== -1
    },

    /**
     * Permission check methods for specific actions
     */
    canViewTask(moduleName) {
      return this.hasTaskPermission(moduleName, 'view')
    },

    canCreateTask(moduleName) {
      return this.hasTaskPermission(moduleName, 'create')
    },

    canUpdateTask(moduleName) {
      return this.hasTaskPermission(moduleName, 'update')
    },

    canAssignTask(moduleName) {
      return this.hasTaskPermission(moduleName, 'assign')
    },

    canUpdateTaskPriority(moduleName) {
      return this.hasTaskPermission(moduleName, 'update_priority')
    },

    canUpdateTaskStatus(moduleName) {
      return this.hasTaskPermission(moduleName, 'update_status')
    },

    canResolveTask(moduleName) {
      return (
        this.canUpdateTaskStatus(moduleName) &&
        this.hasTaskPermission(moduleName, 'resolve')
      )
    },

    canCloseTask(moduleName) {
      return (
        this.canUpdateTaskStatus(moduleName) &&
        this.hasTaskPermission(moduleName, 'close')
      )
    },

    canReopenTask(moduleName) {
      return (
        this.canUpdateTaskStatus(moduleName) &&
        this.hasTaskPermission(moduleName, 'reopen')
      )
    },

    canArchiveTask(moduleName) {
      return this.hasTaskPermission(moduleName, 'archive')
    },

    /**
     * Check if user has any task permissions for the given module
     */
    hasAnyTaskPermissions(moduleName) {
      const permissions = [
        'view',
        'create',
        'update',
        'assign',
        'update_priority',
        'update_status',
        'resolve',
        'close',
        'reopen',
        'archive',
      ]

      return permissions.some((permission) =>
        this.hasTaskPermission(moduleName, permission)
      )
    },

    /**
     * Check if user has all required permissions for a specific action
     * Useful for complex operations that require multiple permissions
     */
    hasRequiredTaskPermissions(moduleName, requiredPermissions) {
      return requiredPermissions.every((permission) =>
        this.hasTaskPermission(moduleName, permission)
      )
    },

    /**
     * Get all available task permissions for a module
     * Useful for debugging or displaying available actions
     */
    getAvailableTaskPermissions(moduleName) {
      const permissions = [
        'view',
        'create',
        'update',
        'assign',
        'update_priority',
        'update_status',
        'resolve',
        'close',
        'reopen',
        'archive',
      ]

      return permissions.filter((permission) =>
        this.hasTaskPermission(moduleName, permission)
      )
    },
  },
}
